import { AnalyticsEventName, FeatureFlags } from '@dupe/common/constants'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import posthog from 'posthog-js'
import React, { useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import { toast } from 'sonner'
import DupeDiscoverPreview from '~/src/components/DupeDiscoverPreview'
import DupeHero from '~/src/components/DupeHero'
import Layout from '~/src/components/Layout'
import LayoutFullHeader from '~/src/components/LayoutFullHeader'
import HomepageInput from '~/src/components/home/HomepageInput'
import GetPaidModal from '~/src/components/rewards/GetPaidModal'
import useAnalytics from '~/src/hooks/useAnalytics'
import useDeviceDetect from '~/src/hooks/useDeviceDetect'
import useIsFeatureOn from '~/src/hooks/useIsFeatureOn'
import useIsSupportedCountries from '~/src/hooks/useIsSupportedCountries'
import { cn } from '~/src/lib/utils'

const HomeCategorySelector = dynamic(
  () => import('~/src/components/home/HomeCategorySelector'),
  {
    ssr: false,
  }
)

const IndexHome: React.FC = () => {
  const analytics = useAnalytics()
  const router = useRouter()
  const hasError = router.query.error
  const isInvalidUrl = hasError && hasError === 'invalid-url'

  const { isMobile } = useDeviceDetect()
  const { isInSupportedCountry } = useIsSupportedCountries(['us'])
  const { featureOn: instagramPaidUsersEnabled } = useIsFeatureOn(
    FeatureFlags.InstagramPaidUsers
  )

  const { ref: inViewRef, inView } = useInView({
    initialInView: true,
    threshold: 0.01,
  })

  // Show error toast if invalid URL
  useEffect(() => {
    if (isInvalidUrl) {
      toast.error('Invalid URL', {
        description: (
          <div className='dark:text-white'>
            Please enter a full product URL and try again.
          </div>
        ),
      })

      // remove the error from the query
      router.replace('/', undefined, { shallow: true })
    }
  }, [isInvalidUrl, router])

  // Start posthog session recording for mobile/us users or instagram paid users
  useEffect(() => {
    // For mobile US users (existing logic)
    const shouldRecordMobileUsers = isMobile && isInSupportedCountry

    // Check if either condition is true (mobile US users OR the Instagram paid users flag is enabled)
    if (shouldRecordMobileUsers || instagramPaidUsersEnabled) {
      posthog.startSessionRecording()

      // If Instagram paid users feature is what triggered this, track it
      if (instagramPaidUsersEnabled) {
        posthog.capture('instagram_paid_user_recording', {
          timestamp: new Date().toISOString(),
        })
      }
    }
  }, [isMobile, isInSupportedCountry, instagramPaidUsersEnabled])

  // trigger analytics event for HomepageViewed
  useEffect(() => {
    analytics.track(AnalyticsEventName.HomepageViewed)
    // The `analytics` object causes this useEffect to
    // run multiple times, so we exclude it from the dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Layout fullWidth hideHeader hideBorders>
      <LayoutFullHeader
        sticky
        transparent={inView}
        className={cn('drop-shadow-sm', {
          'text-white': inView,
          'text-black': !inView,
        })}
      />

      {/* Required to add a blurred backdrop behind the sidebar when on the home page */}
      <div
        className='fixed inset-0 z-10 hidden bg-black/20 backdrop-blur-sm md:block'
        style={{
          width: 'var(--sidebar-width-collapsed)',
        }}
      />

      <div ref={inViewRef} className='relative min-h-screen bg-black'>
        <DupeDiscoverPreview forceAnimation>
          <HomeContent />
        </DupeDiscoverPreview>
      </div>

      <HomeCategorySelector />
      <GetPaidModal />
    </Layout>
  )
}

const HomeContent: React.FC = () => {
  return (
    <div
      className={cn(
        'pointer-events-none sticky top-0 z-10 flex min-h-[100dvh] w-full flex-col items-center md:min-h-screen',
        'flex flex-col justify-center'
      )}
    >
      <div className='flex w-full max-w-screen-sm flex-col space-y-8 px-3 lg:max-w-screen-md'>
        <DupeHero forceTheme={'dark'} className='text-white' />
        <div className='min-h-[186px]'>
          <HomepageInput />
        </div>
      </div>
    </div>
  )
}

export default IndexHome
